// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import DonutChart from '@/views/components/charts/DonutChart';
import { HoldingIcon } from '@/views/components/common';
import { InfoTooltip, Tooltip } from '@/views/components/tooltips';

// Utils
import { FormattedMessage, formatMessage, formatLocalizedString, formatPercentage } from '@/utils/i18n';
import { ESGColourDefinition, ESGRatingClassification, ESGRatingDistributionValue } from '@/enums/Esg';
import ComparisonItem from '@/types/comparison-item';
import Dictionary from '@/types/dictionary';
import AllocationDetails from '@/types/allocation-details';
import Nullable from '@/types/nullable';
import ThemeMode from '@/types/theme-mode';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  selectedItems: Array<ComparisonItem>;
  theme?: ThemeMode;
};

type AllocationData = {
  key?: string;
  label: string;
  value: number;
};

const colorsDict: Dictionary<ESGColourDefinition> = {
  [ESGRatingDistributionValue.AAA]: ESGColourDefinition.VERY_LOW,
  [ESGRatingDistributionValue.AA]: ESGColourDefinition.LOW,
  [ESGRatingDistributionValue.A]: ESGColourDefinition.MODERATE,
  [ESGRatingDistributionValue.BBB]: ESGColourDefinition.LOW_MODERATE,
  [ESGRatingDistributionValue.BB]: ESGColourDefinition.VERY_LOW_MODERATE,
  [ESGRatingDistributionValue.B]: ESGColourDefinition.HIGH,
  [ESGRatingDistributionValue.CCC]: ESGColourDefinition.VERY_HIGH,
  [ESGRatingDistributionValue.NO_VALUE]: ESGColourDefinition.NA,
  Unavailable: ESGColourDefinition.UNAVAILABLE,
} as const;

const ESGRating = ({ selectedItems, theme }: Props) => {
  const hasMultiple = selectedItems.length > 1;
  const isThemeLight = theme === ThemeMode.LIGHT;

  const formatAllocations = (allocations?: Array<AllocationDetails>): Array<AllocationData> => {
    if (!allocations) {
      return Object.values<string>(ESGRatingDistributionValue).map(rating => ({
        key: ESGRatingDistributionValue.NO_VALUE,
        label: rating,
        value: NaN,
      }));
    }

    return allocations
      .map(allocation => ({ key: allocation.name, label: allocation.name, value: allocation.weight }))
      .sort((a, b) => Object.values<string>(ESGRatingDistributionValue).indexOf(a.label) - Object.values<string>(ESGRatingDistributionValue).indexOf(b.label));
  };

  /**
   * For instruments missing ESG data, this reduces the empty values to a single data point for displaying in the donut chart   *
   */
  const formatAllocationsForDonut = (allocations: Array<AllocationData>): Array<AllocationData> =>
    allocations.every(({ key }) => key === ESGRatingDistributionValue.NO_VALUE)
      ? [
          {
            key: 'Unavailable',
            label: 'Unavailable',
            value: 100,
          },
        ]
      : allocations;

  const getClassification = (classification: ESGRatingClassification) =>
    formatMessage(`ESGComparisonTool.Rating.Classification.${ESGRatingClassification[classification]}`);

  const renderAllocationListItems = (allocations: AllocationData[]) =>
    allocations.map(item => {
      const [percentNumber, percentDecimals] = formatPercentage(item.value, 1).split(formatMessage('Common.DecimalSeparator'));
      return (
        <li key={item.label}>
          <span className="dot-reference" style={{ backgroundColor: colorsDict[item.label] }} />
          <p>
            <span className="allocation">
              {item.label === ESGRatingDistributionValue.NO_VALUE ? formatMessage('ESGComparisonTool.Rating.NoData') : item.label}
            </span>
            <span className="border-separator" aria-hidden="true" />
            <span className={classNames('percentage', { 'percentage-null': isNaN(item.value) })}>
              {isNaN(item.value) ? (
                <FormattedMessage id="Common.NotApplicable" />
              ) : (
                <>
                  {percentNumber}
                  <span className="percentage-decimal">
                    {formatMessage('Common.DecimalSeparator')}
                    {percentDecimals}
                  </span>
                </>
              )}
            </span>
          </p>
        </li>
      );
    });

  const renderAllocationDeltaListItems = (items: Array<ComparisonItem>) => {
    const allocations = items.map(item => formatAllocations(item?.esg?.rating?.allocations));

    const delta = Object.values<string>(ESGRatingDistributionValue).map(rating => {
      const [a, b] = allocations;
      const item = {
        label: rating,
        value: (b.find(item => item.label === rating)?.value ?? NaN) - (a.find(item => item.label === rating)?.value ?? NaN),
      };

      return {
        ...item,
        sign: Math.sign(item.value),
      };
    });

    return delta.map(item => {
      let accentColor: Nullable<string>;
      let className: string;
      switch (item.sign) {
        case 1:
          accentColor = ESGColourDefinition.VERY_LOW;
          className = 'positive';
          break;
        case -1:
          accentColor = ESGColourDefinition.VERY_HIGH;
          className = 'negative';
          break;
        case 0:
          accentColor = ESGColourDefinition.MODERATE;
          className = 'neutral';
          break;
        default:
          accentColor = null;
          className = '';
          break;
      }

      return (
        <li key={item.label} className={className} style={{ '--accent-color': accentColor } as React.CSSProperties}>
          <i className={classNames('esg-rating-delta-icon', { 'icon-arrow-thin-left': item.sign !== 0, 'icon-minus-med': item.sign === 0 })} />
          <span className="esg-rating-delta-percentage">
            {isNaN(item.value) ? formatMessage('Common.NotApplicable') : formatPercentage(Math.abs(item.value))}
          </span>
        </li>
      );
    });
  };

  return (
    <div
      className={classNames('esg-rating', {
        multiple: hasMultiple,
      })}
    >
      <div className="esg-rating-title">
        <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
          <FormattedMessage id="ESGComparisonTool.Rating.Title" />
          {hasMultiple ? <InfoTooltip message={formatMessage('ESGComparisonTool.Rating.EducationalText')} className="esg-rating-title-tooltip" /> : null}
        </Typography>
      </div>

      <div className="esg-rating-data-wrapper">
        {selectedItems.map((item, index) => {
          const allocations = formatAllocations(item.esg?.rating?.allocations);
          return (
            <div className="esg-rating-data" key={item._id}>
              {hasMultiple ? (
                <Tooltip message={formatLocalizedString(item.name)} className="esg-rating-holding">
                  <span>
                    <HoldingIcon type={item.type} color={item.color} />
                  </span>
                </Tooltip>
              ) : null}
              <div className={classNames('esg-rating-donut', { 'is-single': !hasMultiple })}>
                <DonutChart
                  data={formatAllocationsForDonut(allocations)}
                  key="esg-rating-donut-chart"
                  size={190}
                  colorsDict={{ ...colorsDict, ...(theme === ThemeMode.LIGHT && { Unavailable: ESGColourDefinition.UNAVAILABLE_THEME_LIGHT }) }}
                  innerSize="106"
                  borderWidth={2}
                  borderColor={isThemeLight ? '#FFFFFF' : '#191E36'}
                  animate={false}
                  mouseTracking={false}
                />

                <div
                  className="esg-rating-donut-legend"
                  style={{ '--accent-color': colorsDict[item?.esg?.rating?.value || ESGColourDefinition.TEXT_BASE_2] } as React.CSSProperties}
                >
                  <p className={classNames('esg-rating-donut-legend-title', { 'not-applicable': !item?.esg?.rating?.value })}>
                    {!item?.esg?.rating?.value
                      ? formatMessage('Common.NotApplicable')
                      : item.esg.rating.value === ESGRatingDistributionValue.NO_VALUE
                        ? formatMessage('ESGComparisonTool.Rating.NoData')
                        : item.esg.rating.value}
                  </p>
                  {item?.esg?.rating?.value && item.esg.rating.classification && (
                    <p className="esg-rating-donut-legend-description">{getClassification(item?.esg?.rating?.classification)}</p>
                  )}
                </div>
              </div>
              <ul
                className={classNames('esg-rating-percentages', {
                  'no-legend': index > 0,
                })}
              >
                {renderAllocationListItems(allocations)}
              </ul>
            </div>
          );
        })}
        {hasMultiple ? (
          <div className="esg-rating-delta">
            <ul className="esg-rating-delta-percentages">{renderAllocationDeltaListItems(selectedItems)}</ul>
          </div>
        ) : null}
      </div>
      {hasMultiple ? null : (
        <p className="esg-rating-disclaimer">
          <FormattedMessage id="ESGComparisonTool.Rating.EducationalText" />
        </p>
      )}
    </div>
  );
};

export default ESGRating;
